import React, { useCallback } from 'react';
import { useStore } from 'react-admin';
import { useAutoSave, UseAutoSaveOptions } from 'src/hooks/useAutoSave';
import { AutoSaveStoredData } from 'src/constants/autoSave';
import { safeClearStorage } from 'src/utils/autoSaveErrorHandler';

/**
 * Component that listens to form changes and triggers auto-save
 * Must be placed inside a form context (TabbedForm, SimpleForm, etc.)
 *
 * @example
 * ```tsx
 * // Basic usage (enabled by default)
 * <TabbedForm>
 *   <AutoSaveFormListener storageKey="my-form" />
 *   <TextInput source="name" />
 * </TabbedForm>
 *
 * // With custom options
 * <AutoSaveFormListener
 *   storageKey="my-form"
 *   debounceMs={1000}
 *   enabled={false}
 * />
 * ```
 */
export const AutoSaveFormListener: React.FC<UseAutoSaveOptions> = (props) => {
    useAutoSave(props);

    // This component doesn't render anything
    return null;
};

/**
 * Hook to get clearAutoSave function for use in onSuccess callbacks
 *
 * @param { string } storageKey - The key used for storing auto-save data
 * @returns Object with clearAutoSave function
 *
 */
export const useClearAutoSave = (storageKey: string) => {
    const [, setStoredData] = useStore<AutoSaveStoredData | undefined>(storageKey, undefined);

    const clearAutoSave = useCallback(() => {
        if (!storageKey) return; // Don't clear if no valid storage key
        safeClearStorage(setStoredData, 'Clear auto-save data (useClearAutoSave)');
    }, [storageKey, setStoredData]);

    return { clearAutoSave };
};

export default AutoSaveFormListener;
